import { CircleNotch, FileText } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ReportType } from "@/api/districts/reports";
import { useGenerateReport } from "@/api/districts/reports";
import type { DateRange } from "@/components/dashboard/date-range-picker";
import {
	DateRangePicker,
	getDefaultDateRange,
} from "@/components/dashboard/date-range-picker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useDistrict } from "@/contexts/district-context";
import { useHideElectricity } from "@/hooks/use-hide-electricity";

type MeterTypeOption = {
	type: ReportType;
	label: string;
	successLabel: string;
};

interface GenerateReportDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function GenerateReportDialog({
	open,
	onOpenChange,
}: GenerateReportDialogProps) {
	const { t } = useTranslation();
	const { districtId } = useDistrict();
	const { generate, isLoading } = useGenerateReport();
	const hideElectricity = useHideElectricity();

	const ALL_METER_TYPES: MeterTypeOption[] = [
		{
			type: "water",
			label: t("report.typeWater"),
			successLabel: t("report.typeWaterLabel"),
		},
		{
			type: "heat",
			label: t("report.typeHeat"),
			successLabel: t("report.typeHeatLabel"),
		},
		{
			type: "electricity",
			label: t("report.typeElectricity"),
			successLabel: t("report.typeElectricityLabel"),
		},
	];

	const METER_TYPES = hideElectricity
		? ALL_METER_TYPES.filter((m) => m.type !== "electricity")
		: ALL_METER_TYPES;

	const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
	const [selectedTypes, setSelectedTypes] = useState<Set<ReportType>>(
		new Set(),
	);

	useEffect(() => {
		if (open) {
			setDateRange(getDefaultDateRange());
			setSelectedTypes(new Set());
		}
	}, [open]);

	function toggleType(type: ReportType) {
		setSelectedTypes((prev) => {
			const next = new Set(prev);
			if (next.has(type)) {
				next.delete(type);
			} else {
				next.add(type);
			}
			return next;
		});
	}

	async function handleDownload() {
		if (!districtId || selectedTypes.size === 0) return;
		try {
			await generate(Array.from(selectedTypes), {
				district_id: districtId,
				start_date: dateRange.start_date,
				end_date: dateRange.end_date,
			});
			for (const option of METER_TYPES) {
				if (selectedTypes.has(option.type)) {
					toast.success(t("report.downloaded", { type: option.successLabel }));
				}
			}
		} catch {
			toast.error(t("report.downloadError"));
		}
	}

	const isDownloadDisabled =
		isLoading || !districtId || selectedTypes.size === 0;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("report.title")}</DialogTitle>
					<DialogDescription>{t("report.description")}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-6 py-2">
					<div className="flex flex-col gap-1.5">
						<Label>{t("report.dateRange")}</Label>
						<DateRangePicker value={dateRange} onChange={setDateRange} />
					</div>

					<div className="flex flex-col gap-3">
						<Label>{t("report.meterTypes")}</Label>
						{METER_TYPES.map(({ type, label }) => (
							<div key={type} className="flex items-center gap-2">
								<Checkbox
									id={`report-type-${type}`}
									checked={selectedTypes.has(type)}
									onCheckedChange={() => toggleType(type)}
								/>
								<Label
									htmlFor={`report-type-${type}`}
									className="cursor-pointer font-normal"
								>
									{label}
								</Label>
							</div>
						))}
						{selectedTypes.size === 0 && (
							<p className="text-destructive text-xs">
								{t("report.minOneType")}
							</p>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button
						className="w-full gap-1.5"
						disabled={isDownloadDisabled}
						onClick={handleDownload}
					>
						{isLoading ? (
							<CircleNotch className="size-4 animate-spin" aria-hidden="true" />
						) : (
							<FileText className="size-4" aria-hidden="true" />
						)}
						{t("report.download")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
